
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return; // Wait until auth state is determined
    }
    
    // Allow access to Knowledge Hub, FAQs, and related pages regardless of authentication
    if (location.pathname === "/knowledge-hub" || 
        location.pathname.startsWith("/knowledge-hub/") ||
        location.pathname === "/crypto-basics" ||
        location.pathname === "/glossary" ||
        location.pathname === "/faqs") {
      setInitialized(true);
      return;
    }
    
    if (!isAuthenticated) {
      // Store current path as redirect URL before navigating to auth page
      if (location.pathname !== '/auth') {
        localStorage.setItem('redirectUrl', location.pathname + location.search);
      }
      
      toast({
        title: "Authentication Required",
        description: "Please log in to access this page.",
        variant: "destructive",
      });
      
      navigate('/auth');
    }
    
    setInitialized(true);
  }, [isAuthenticated, isLoading, location.pathname, location.search, navigate, toast]);

  if (isLoading || !initialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#517FA4] mb-3"></div>
        <div className="text-[#517FA4] font-medium">Verifying authentication...</div>
      </div>
    );
  }

  // Allow Knowledge Hub access without authentication
  if (location.pathname === "/knowledge-hub" || 
      location.pathname.startsWith("/knowledge-hub/") ||
      location.pathname === "/crypto-basics" ||
      location.pathname === "/glossary" ||
      location.pathname === "/faqs") {
    return <>{children}</>;
  }

  return isAuthenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
